@let errorMeasureEnabled = this.errorMeasureFeatureFlag().enabled;
@let futureErrorMeasureEnabled = this.futureErrorMeasureFeatureFlag().enabled;

<div class="flex flex-col gap-2">
  <div class="flex items-center justify-between gap-2">
    <div class="flex h-8 items-center border-l-2 border-border-subdued pl-3">
      <pm-h2 elementClass="text-sm font-bold leading-[1.3]">
        {{ 'RESULT.KPIS.TITLE_BAR.FORECAST_ERROR' | translate }}
      </pm-h2>
    </div>

    @if (errorMeasureEnabled || futureErrorMeasureEnabled) {
      <div class="flex items-center gap-2">
        <pm-form-field
          class="block w-[216px]"
          label="RESULT.KPIS.TITLE_BAR.ERROR_MEASURE_LABEL"
        >
          <pm-form-input-select
            size="sm"
            data-testid="measure-select"
            [ngModel]="selectedMeasure()"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onMeasureChange($event)"
            [data]="measureOptions()"
            [allowUnselect]="false"
          >
            <div
              *pmFormInputSelection="let option = node"
              class="flex w-full items-center gap-2"
            >
              <span class="min-w-0 flex-1 truncate">
                {{ option.label }}
              </span>
              <pm-icon
                class="shrink-0 text-content-subdued"
                size="xs"
                [icon]="infoIcon"
                pmTooltipPosition="above"
                [pmTooltip]="option.description"
              />
            </div>
          </pm-form-input-select>
        </pm-form-field>

        @if (errorMeasureEnabled) {
          <pm-button
            variant="outlined"
            size="normal"
            dataTestid="measure-settings-menu"
            [icon]="settingsIcon"
            [matMenuTriggerFor]="measureMenu.menu"
          />
          <pm-menu
            #measureMenu
            xPosition="before"
            yPosition="above"
            panelClass="!max-w-none [&_.mat-mdc-menu-content]:!p-0"
          >
            <div class="flex w-[320px] flex-col gap-4 px-4 py-3">
              <div class="flex flex-col gap-3">
                <h4
                  class="text-xs font-bold leading-[1.3] text-content-interactive-subdued"
                >
                  {{ 'RESULT.KPIS.AGGREGATION.TITLE' | translate }}
                </h4>

                <pm-tabs-group
                  size="normal"
                  [fullWidth]="true"
                  [options]="aggregationLevelTabs"
                  [queryKey]="undefined"
                  [initialValue]="aggregationLevel()"
                  dataTestidPrefix="aggregation-level"
                  (handleClick)="onAggregationLevelChange($event)"
                />

                <div
                  class="grid text-[10px] leading-[1.38] text-content-default"
                >
                  <p
                    class="col-start-1 row-start-1"
                    [class.invisible]="isCustomAggregation()"
                  >
                    {{
                      'RESULT.KPIS.AGGREGATION.HIGHEST_DESCRIPTION' | translate
                    }}
                  </p>
                  <p
                    class="col-start-1 row-start-1"
                    [class.invisible]="!isCustomAggregation()"
                  >
                    {{
                      'RESULT.KPIS.AGGREGATION.CUSTOM_DESCRIPTION' | translate
                    }}
                  </p>
                </div>

                <div
                  [class.invisible]="!isCustomAggregation()"
                  [attr.inert]="isCustomAggregation() ? null : ''"
                >
                  <pm-form-input-select
                    size="sm"
                    data-testid="aggregation-level-select"
                    [multiSelect]="true"
                    [allowSelectAll]="true"
                    [clearable]="true"
                    [allowUnselect]="true"
                    [data]="aggregationLevelOptions()"
                    [placeholder]="'RESULT.KPIS.AGGREGATION.SELECT_LEVEL'"
                    [ngModel]="selectedAggregationLevels()"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="selectedAggregationLevels.set($event)"
                  />
                </div>
              </div>

              <pm-button
                class="self-end"
                variant="primary"
                size="normal"
                text="RESULT.KPIS.TITLE_BAR.CALCULATE"
                dataTestid="measure-calculate"
                [disabled]="isCalculateDisabled()"
                [isLoading]="isAnyMeasureCalculating()"
                (handleClick)="onCalculate()"
              />
            </div>
          </pm-menu>
        }
      </div>
    }
  </div>

  <div class="flex flex-col px-3">
    <div class="flex h-11 items-center border-b border-border-subdued">
      <div
        class="min-w-0 flex-1 truncate text-xs font-medium text-content-title"
      >
        {{ 'RESULT.KPIS.TITLE_BAR.TIME_SERIES' | translate }}
      </div>

      @if (errorMeasureEnabled) {
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <span class="truncate text-xs font-medium text-content-title">
            {{ 'RESULT.KPIS.TITLE_BAR.SELECTED_VERSION' | translate }}
          </span>
          <pm-icon
            class="shrink-0 text-content-subdued"
            [icon]="infoIcon"
            [pmTooltip]="'RESULT.KPIS.TITLE_BAR.ERROR_MEASURE_TOOLTIP'"
          />
        </div>
      }

      @if (futureErrorMeasureEnabled) {
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <span class="truncate text-xs font-medium text-content-title">
            {{ 'RESULT.KPIS.TITLE_BAR.PAST_VERSIONS' | translate }}
          </span>
          <pm-icon
            class="shrink-0 text-content-subdued"
            [icon]="infoIcon"
            [pmTooltip]="'RESULT.KPIS.TITLE_BAR.AGGR_ERROR_MEASURE_TOOLTIP'"
          />
        </div>
      }
    </div>

    @for (
      forecastSeries of [latestForecastSeries$ | async];
      track forecastSeries?.resultSetId
    ) {
      @if (forecastSeries) {
        <div class="flex h-11 items-center">
          <div class="flex min-w-0 flex-1 items-center gap-1.5">
            <span
              class="size-2 shrink-0 rounded-full"
              [ngStyle]="{ 'background-color': color }"
            ></span>
            <span class="truncate text-xs text-content-default">
              {{ getNameOfForecastSeries(forecastSeries) | translate }}
            </span>
          </div>

          @if (errorMeasureEnabled) {
            <div class="flex min-w-0 flex-1 items-center gap-1 text-xs">
              @if (isEstimatedErrorMeasureCalculating()) {
                <mat-spinner [diameter]="18" />
              } @else if (estimatedErrorMeasure() !== null) {
                @let errorMeasureValue = estimatedErrorMeasure()!;
                <strong
                  class="font-bold text-content-default"
                  data-testid="measure-values"
                >
                  {{
                    errorMeasureValue
                      | number: '1.2-2' : translateService.getCurrentLang()
                  }}
                </strong>
                @if (showEstimatedAccuracy()) {
                  <span class="truncate text-content-subdued">
                    {{
                      'RESULT.KPIS.ACCURACY'
                        | translate
                          : {
                              value:
                                accuracyOf(errorMeasureValue)
                                | number
                                  : '1.2-2'
                                  : translateService.getCurrentLang(),
                            }
                    }}
                  </span>
                }
              } @else {
                <span class="text-content-subdued">
                  {{ 'UTILITY.PLACEHOLDER_NO_DATA_AVAILABLE' | translate }}
                </span>
              }
            </div>
          }

          @if (futureErrorMeasureEnabled) {
            <div class="flex min-w-0 flex-1 items-center gap-1 text-xs">
              @if (isCalculatingFutureMeasure()) {
                <mat-spinner [diameter]="18" />
              } @else if (selectedFutureErrorMeasureValue(); as futureMeasure) {
                <strong class="font-bold text-content-default">
                  {{
                    futureMeasure
                      | number: '1.2-2' : translateService.getCurrentLang()
                  }}
                </strong>
                @if (showFutureAccuracy()) {
                  <span class="truncate text-content-subdued">
                    {{
                      'RESULT.KPIS.ACCURACY'
                        | translate
                          : {
                              value:
                                accuracyOf(futureMeasure)
                                | number
                                  : '1.2-2'
                                  : translateService.getCurrentLang(),
                            }
                    }}
                  </span>
                }
              } @else {
                <span class="text-content-subdued">
                  {{ 'UTILITY.PLACEHOLDER_NO_DATA_AVAILABLE' | translate }}
                </span>
              }
            </div>
          }
        </div>
      }
    }
  </div>
</div>
